import { Alert, Box, Button, Stack, Tab, Tabs, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { useSearchParams } from "react-router";
import { useSendMagicLink, type MagicLinkPayload } from "../hooks/useApiQueries.js";
import { SEO } from "../components/SEO.js";
import { neonButtonSx } from "../components/Neon.js";

const API_BASE = import.meta.env.VITE_API_URL || "";

const ERROR_MESSAGES: Record<string, string> = {
  invalid: "That sign-in link is invalid or expired. Request a new one below.",
  oauth: "Google sign-in didn't complete. Try again or use the magic link.",
  "not-allowed": "That account isn't authorised to sign in. Ask the Corian Park team to add it.",
};

type Mode = "individual" | "company";

export function SignIn() {
  const [params] = useSearchParams();
  const mutation = useSendMagicLink();
  const errorKey = params.get("error");
  const errorMsg = errorKey ? ERROR_MESSAGES[errorKey] ?? "Something went wrong. Try again." : null;

  // ?type=company deep-links from About → "Set up a Company account" CTA.
  const initialMode: Mode = params.get("type") === "company" ? "company" : "individual";
  const [mode, setMode] = useState<Mode>(initialMode);

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [abn, setAbn] = useState("");
  const [website, setWebsite] = useState("");

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !name) return;
    if (mode === "company" && !companyName) return;
    const payload: MagicLinkPayload = {
      email,
      name,
      accountType: mode === "company" ? "COMPANY" : "INDIVIDUAL",
    };
    if (mode === "company") {
      payload.company = {
        name: companyName,
        abn: abn || undefined,
        website: website || undefined,
      };
    }
    await mutation.mutateAsync(payload);
  };

  return (
    <>
      <SEO
        title="Sign in"
        description="Sign in to Corian Park with a magic link."
        canonical="https://corianpark.au/auth/sign-in"
        noindex
      />
      <Box sx={{ maxWidth: 480, mx: "auto", py: 6 }}>
        <Typography variant="h2" sx={{ fontSize: 36, mb: 1 }}>Sign in or create a profile</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Free. No password. We email you a one-time link to sign in or set up your account.
        </Typography>

        {errorMsg && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMsg}
          </Alert>
        )}

        {mutation.isSuccess ? (
          <Alert severity="success" data-testid="magic-link-sent">
            Check your email — we sent you a sign-in link. It expires in 30 minutes.
          </Alert>
        ) : (
          <>
            <Tabs
              value={mode}
              onChange={(_, v) => setMode(v as Mode)}
              sx={{ mb: 3, borderBottom: "1px solid", borderColor: "divider" }}
            >
              <Tab value="individual" label="Individual" />
              <Tab value="company" label="Company" />
            </Tabs>

            <Box component="form" onSubmit={onSubmit}>
              <Stack spacing={2}>
                <TextField
                  label="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  fullWidth
                />
                <TextField
                  type="email"
                  label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  fullWidth
                />

                {mode === "company" && (
                  <>
                    <TextField
                      label="Company name"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      required
                      fullWidth
                    />
                    <TextField
                      label="ABN (optional)"
                      value={abn}
                      onChange={(e) => setAbn(e.target.value.replace(/[^\d]/g, "").slice(0, 11))}
                      slotProps={{ htmlInput: { inputMode: "numeric", maxLength: 11 } }}
                      fullWidth
                      helperText="11 digits"
                    />
                    <TextField
                      type="url"
                      label="Website (optional)"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                      placeholder="https://example.com"
                      fullWidth
                    />
                    <Typography variant="caption" color="text.secondary">
                      Company accounts get staff access and content promotion options once your
                      profile is set up.
                    </Typography>
                  </>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={mutation.isPending}
                  sx={neonButtonSx}
                  fullWidth
                >
                  {mutation.isPending
                    ? "Sending…"
                    : mode === "company"
                    ? "Email me a sign-in link (Company)"
                    : "Email me a sign-in link"}
                </Button>

                <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
                  or{" "}
                  <a href={`${API_BASE}/v1/auth/google/start`} style={{ color: "inherit" }}>
                    continue with Google
                  </a>
                  {" "}(individuals only)
                </Typography>
              </Stack>
            </Box>
          </>
        )}
      </Box>
    </>
  );
}
